import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  BarChart3, Store, Phone, Table, Users, QrCode, RefreshCw, Download, X, Images, Palette
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import OverviewTab from './OverviewTab';
import PartnersListTab from './PartnersListTab';
import AgentsTab from './AgentsTab';
import InvestmentTablesTab from './InvestmentTablesTab';
import LeadsTab from './LeadsTab';
import ScansTab from './ScansTab';
import SuccessCasesTab from './SuccessCasesTab';
import QRCodeComposerModal from './QRCodeComposerModal';
import { API } from '../../../lib/api';

const SITE_URL = window.location.origin;

const PARTNER_TABS = {
  OVERVIEW: 'overview',
  LIST: 'list',
  AGENTS: 'agents',
  TABLES: 'tables',
  CASES: 'cases',
  LEADS: 'leads',
  SCANS: 'scans',
};

const PartnersTab = ({ getAuthHeaders, navigate, location }) => {
  const getInitialPartnerTab = () => {
    const params = new URLSearchParams(location?.search || '');
    const t = params.get('partnerTab');
    return Object.values(PARTNER_TABS).includes(t) ? t : PARTNER_TABS.OVERVIEW;
  };

  const [partnerTab, setPartnerTab] = useState(getInitialPartnerTab);
  const [partners, setPartners] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tables, setTables] = useState([]);
  const [successCases, setSuccessCases] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrModal, setQrModal] = useState(null);
  const [composerModal, setComposerModal] = useState(null);
  const qrRef = useRef(null);

  const handleSetPartnerTab = (tab) => {
    setPartnerTab(tab);
    const params = new URLSearchParams(location?.search || '');
    params.set('partnerTab', tab);
    navigate({ search: params.toString() }, { replace: true });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [partnerRes, agentRes, tableRes, casesRes, overviewRes] = await Promise.all([
        axios.get(`${API}/partners`, getAuthHeaders()),
        axios.get(`${API}/agents`, getAuthHeaders()),
        axios.get(`${API}/investment-tables`, getAuthHeaders()),
        axios.get(`${API}/success-cases`),
        axios.get(`${API}/partners-overview`, getAuthHeaders()),
      ]);
      setPartners(partnerRes.data);
      setAgents(agentRes.data);
      setTables(tableRes.data);
      setSuccessCases(casesRes.data);
      setOverview(overviewRes.data);
    } catch {
      toast.error('Erro ao carregar dados de parcerias');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
    img.onload = () => {
      canvas.width = img.width * 3;
      canvas.height = img.height * 3;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const a = document.createElement('a');
      a.download = `qrcode-${qrModal?.slug || 'parceiro'}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Carregando parcerias...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: PARTNER_TABS.OVERVIEW, label: 'Dashboard', Icon: BarChart3 },
          { key: PARTNER_TABS.LIST, label: 'Parceiros', Icon: Store },
          { key: PARTNER_TABS.AGENTS, label: 'Agentes', Icon: Phone },
          { key: PARTNER_TABS.TABLES, label: 'Tabelas de Investimento', Icon: Table },
          { key: PARTNER_TABS.CASES, label: 'Cases de Sucesso', Icon: Images },
          { key: PARTNER_TABS.LEADS, label: 'Leads', Icon: Users },
          { key: PARTNER_TABS.SCANS, label: 'Leituras de QR', Icon: QrCode },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => handleSetPartnerTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              partnerTab === key
                ? 'bg-orange-600 text-white shadow-md'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-orange-50 hover:text-orange-600'
            }`}
          >
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
        <button onClick={fetchAll} className="ml-auto px-3 py-2 text-gray-400 hover:text-orange-600 transition-colors" title="Atualizar">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {partnerTab === PARTNER_TABS.OVERVIEW && <OverviewTab overview={overview} partners={partners} />}
      {partnerTab === PARTNER_TABS.LIST && (
        <PartnersListTab
          partners={partners} agents={agents} tables={tables}
          getAuthHeaders={getAuthHeaders} onRefresh={fetchAll} onShowQR={setQrModal}
        />
      )}
      {partnerTab === PARTNER_TABS.AGENTS && (
        <AgentsTab agents={agents} getAuthHeaders={getAuthHeaders} onRefresh={fetchAll} />
      )}
      {partnerTab === PARTNER_TABS.TABLES && (
        <InvestmentTablesTab tables={tables} getAuthHeaders={getAuthHeaders} onRefresh={fetchAll} />
      )}
      {partnerTab === PARTNER_TABS.CASES && (
        <SuccessCasesTab successCases={successCases} getAuthHeaders={getAuthHeaders} onRefresh={fetchAll} />
      )}
      {partnerTab === PARTNER_TABS.LEADS && (
        <LeadsTab partners={partners} getAuthHeaders={getAuthHeaders} />
      )}
      {partnerTab === PARTNER_TABS.SCANS && (
        <ScansTab partners={partners} getAuthHeaders={getAuthHeaders} />
      )}

      {/* QR Code Modal */}
      {qrModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-gray-800 text-lg">QR Code — {qrModal.name}</h3>
                <button onClick={() => setQrModal(null)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div ref={qrRef} className="flex justify-center mb-4 p-4 bg-white rounded-xl border">
                <QRCodeSVG value={`${SITE_URL}/parceiro/${qrModal.slug}`} size={220} includeMargin level="H" />
              </div>
              <p className="text-xs text-gray-500 text-center mb-1 break-all">
                {SITE_URL}/parceiro/{qrModal.slug}
              </p>
              <p className="text-xs text-gray-400 text-center mb-4">"Deixe o sol pagar sua próxima rodada"</p>
              <Button onClick={downloadQR} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                <Download className="w-4 h-4 mr-2" />Baixar QR Code (PNG)
              </Button>
              <Button
                onClick={() => { setComposerModal(qrModal); setQrModal(null); }}
                className="w-full mt-2 bg-white border border-orange-300 text-orange-700 hover:bg-orange-50"
                variant="outline"
              >
                <Palette className="w-4 h-4 mr-2" />Criar Arte com QR Code
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* QR Code Composer Modal */}
      {composerModal && (
        <QRCodeComposerModal
          partner={composerModal}
          allPartners={partners}
          onClose={() => setComposerModal(null)}
        />
      )}
    </div>
  );
};

export default PartnersTab;
